// @flow
import { HTTP_METHODS } from "../constants/Constants";

import universalFetch from "../utils/fetch/universalFetch";
import Cache from "../utils/browser/Cache";

import { UnauthorizedException } from "../exceptions";
import {
  INTERNAL_LOGIN_TYPE,
  loginType,
  loginPath,
  loginPasswordField,
  loginUsernameField,
  logoutPath,
  getBasePath,
} from "../constants";

/**
 */
class Authenticate {
  _isBasic: boolean;

  /**
   */
  constructor() {
    this._isBasic = false;
  }

  /**
   */
  get authenticationType(): $Keys<typeof INTERNAL_LOGIN_TYPE> {
    const type = loginType();

    if (!type || type === INTERNAL_LOGIN_TYPE.JAAS) {
      return INTERNAL_LOGIN_TYPE.JAAS;
    }

    if (type.includes("FormClient")) {
      return INTERNAL_LOGIN_TYPE.PAC4J_FORM;
    } else if (type.includes("IndirectBasicClient")) {
      return INTERNAL_LOGIN_TYPE.PAC4J_BASIC;
    }
    return INTERNAL_LOGIN_TYPE.PAC4J_INDIRECT;
  }

  /**
   */
  get isBasicAuthentication(): boolean {
    return (
      this._isBasic ||
      this.authenticationType === INTERNAL_LOGIN_TYPE.PAC4J_BASIC
    );
  }

  /**
   */
  set isBasicAuthentication(isBasicAuthentication: boolean) {
    this._isBasic = isBasicAuthentication;
  }

  /**
   */
  requestLogin(): Promise<any> {
    return universalFetch({
      url: `${getBasePath()}/login`,
      method: HTTP_METHODS.GET,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  }

  /**
   */
  initLogin(initLogin: boolean): Promise<any> {
    if (this.isBasicAuthentication || !initLogin) {
      return Promise.resolve(true);
    }

    return this.requestLogin().catch((error) => {
      // when an unauthorizedexception is received on a request to the login service,
      // this indicates a basic authorization scenario
      // in case of form based authentication other exceptions are thrown
      if (error instanceof UnauthorizedException) {
        this.isBasicAuthentication = true;
      }

      return Promise.resolve({});
    });
  }

  /**
   */
  login(username: string, password: string): Promise<any> {
    switch (this.authenticationType) {
      case INTERNAL_LOGIN_TYPE.PAC4J_FORM:
        return this.doFormLogin(username, password);
      case INTERNAL_LOGIN_TYPE.PAC4J_BASIC:
        return this.doBasicAuthentication(username, password);
      case INTERNAL_LOGIN_TYPE.PAC4J_INDIRECT:
        // pac4j should handle this
        return Promise.resolve();
      default:
        return this.doJaasAuthentication(username, password);
    }
  }

  /**
   */
  getFormLoginUrl(): string {
    switch (this.authenticationType) {
      case INTERNAL_LOGIN_TYPE.PAC4J_BASIC:
        return `${getBasePath()}${loginPath("DirectBasicAuthClient")}`;
      case INTERNAL_LOGIN_TYPE.PAC4J_FORM:
        return `${getBasePath()}${loginPath("FormClient")}`;
      case INTERNAL_LOGIN_TYPE.PAC4J_INDIRECT:
        //pac4j should handle this
        return "";
      default:
        return `${getBasePath()}/j_security_check`;
    }
  }

  /**
   */
  getFormLoginData(username: string, password: string): string {
    const encodedUsername = encodeURIComponent(username);
    const encodedPassword = encodeURIComponent(password);

    switch (this.authenticationType) {
      case INTERNAL_LOGIN_TYPE.PAC4J_BASIC:
      case INTERNAL_LOGIN_TYPE.PAC4J_FORM:
        return `${loginUsernameField()}=${encodedUsername}&${loginPasswordField()}=${encodedPassword}`;
      case INTERNAL_LOGIN_TYPE.JAAS:
        return `j_username=${encodedUsername}&j_password=${encodedPassword}`;
      default:
        return "";
    }
  }

  /**
   */
  doFormLogin(username: string, password: string): Promise<any> {
    return universalFetch({
      url: this.getFormLoginUrl(),
      method: HTTP_METHODS.POST,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      data: this.getFormLoginData(username, password),
    });
  }

  /**
   */
  doBasicAuthentication(username: string, password: string): Promise<any> {
    Cache.addItem("basic", btoa(`${username}:${password}`));
    return this.requestLogin();
  }

  /**
   */
  doJaasAuthentication(username: string, password: string): Promise<any> {
    return this.initLogin(true).then(() => {
      if (this.isBasicAuthentication) {
        Cache.removeItem("basic-ok");
        return this.doBasicAuthentication(username, password).then(() => {
          Cache.addItem("basic-ok", true);
        });
      }
      return this.doFormLogin(username, password);
    });
  }

  /**
   */
  getLogoutUrl(): string {
    const type = loginType();

    if (!type || type === INTERNAL_LOGIN_TYPE.JAAS) {
      return `${getBasePath()}/Logoff`;
    }

    return `${getBasePath()}${logoutPath()}`;
  }

  /**
   * Browser redirect to the logout url. (no xmlhttp)
   */
  redirectToLogout(postRedirectUrl: string = ""): void {
    Cache.clear();

    // $FlowIssue
    const origin = globalThis.location.origin;
    const redirectUrl = postRedirectUrl?.startsWith("http")
      ? postRedirectUrl
      : `${origin}${getBasePath()}`;

    // $FlowIssue
    globalThis.location.assign(this.getLogoutUrl() + "?url=" + redirectUrl);
  }

  /**
   * Logout the user with xmlhttp
   */
  logout(): Promise<any> {
    return universalFetch({
      url: this.getLogoutUrl(),
    }).then((response) => {
      // clear cache because of cached contributions
      Cache.clear();
      return response;
    });
  }
}

export default Authenticate;
